'use client';

import React, { useState } from 'react';
import { AnimatePresence, m } from 'motion/react';
import { css, cx } from 'styled-system/css';
import { card, selection } from 'styled-system/recipes';

import { ClipUp, Loop, useDesignMotion } from '@shaojiang/components/motion';
import { Media } from '@shaojiang/components/site/Media';
import { fill } from '@shaojiang/content';
import type { Feature } from '@shaojiang/types/profile';

interface FeatureExplorerProps {
  features: Feature[];
  /** Shown before a feature is picked */
  cover: { src: string; alt: string };
  /** "Feature {n}" */
  kicker: string;
  /** Rendered under the feature panel (stats) */
  below?: React.ReactNode;
  /** The sticky side card */
  aside: React.ReactNode;
}

const tabStyle = css({
  px: '3.5',
  py: '2',
  borderRadius: 'l2',
  textStyle: 'xs',
  fontWeight: '500',
  bg: 'transparent',
  border: 'none',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  transition: 'color {durations.base}',
});

const tabs = selection();

/**
 * A big screenshot that follows the chosen feature tab, with the feature's story underneath.
 * Screenshot and text cross-fade; the tab marker slides.
 */
export function FeatureExplorer({ features, cover, kicker, below, aside }: FeatureExplorerProps) {
  const [active, setActive] = useState(0);
  const motion = useDesignMotion();
  const feature = features[active];
  const image = feature ? { src: feature.url, alt: feature.name } : cover;

  return (
    <>
      <div className={css({ position: 'relative' })}>
        <Loop
          preset='drift'
          aria-hidden
          className={css({
            position: 'absolute',
            w: '80%',
            h: '80%',
            left: '10%',
            top: '20%',
            borderRadius: 'full',
            bg: 'radial-gradient(circle, {colors.glow}, transparent 62%)',
            filter: 'blur(24px)',
            pointerEvents: 'none',
          })}
        />
        <ClipUp immediate delay={0.2} className={cx(card().root, css({ borderRadius: 'panel' }))}>
          <div className={css({ position: 'relative', aspectRatio: '16 / 9', m: '2.5' })}>
            <AnimatePresence initial={false} mode='popLayout'>
              <m.div
                key={image.src}
                className={css({ position: 'absolute', inset: '0' })}
                initial={{ opacity: 0, scale: 1.02 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              >
                <Media
                  src={image.src}
                  alt={image.alt}
                  priority={active === 0}
                  sizes='100vw'
                  className={css({ h: 'full', borderRadius: 'calc({radii.panel} - 10px)' })}
                />
              </m.div>
            </AnimatePresence>
          </div>
        </ClipUp>
      </div>

      <div
        className={css({
          display: 'grid',
          gap: '8',
          alignItems: 'start',
          gridTemplateColumns: { base: '1fr', lg: '8fr 4fr' },
        })}
      >
        <div className={css({ display: 'flex', flexDirection: 'column', gap: '5', minW: '0' })}>
          {features.length > 1 && (
            <div
              role='tablist'
              aria-label={fill(kicker, { n: '' }).trim()}
              className={cx(
                card().root,
                css({
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  gap: '1.5',
                  p: '1.5',
                  alignSelf: 'flex-start',
                  borderRadius: 'media',
                  maxW: 'full',
                }),
              )}
            >
              {features.map((f, i) => (
                <button
                  key={f.name}
                  type='button'
                  role='tab'
                  aria-selected={i === active}
                  onClick={() => setActive(i)}
                  className={cx(tabStyle, tabs.control)}
                >
                  {i === active && (
                    <m.span
                      layoutId='feature-tab'
                      className={tabs.indicator}
                      transition={motion.layout}
                    />
                  )}
                  {f.name}
                </button>
              ))}
            </div>
          )}
          {feature && (
            <div className={cx(card().root, css({ p: '7', gap: '2.5', overflow: 'visible' }))}>
              <AnimatePresence mode='wait' initial={false}>
                <m.div
                  key={feature.name}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  className={css({ display: 'flex', flexDirection: 'column', gap: '2.5' })}
                >
                  <p className={css({ textStyle: 'label', color: 'fg.accent' })}>
                    {fill(kicker, { n: String(active + 1).padStart(2, '0') })}
                  </p>
                  <p className={css({ textStyle: 'h3' })}>{feature.name}</p>
                  <p className={css({ textStyle: 'body', color: 'fg.muted' })}>
                    {feature.description}
                  </p>
                </m.div>
              </AnimatePresence>
            </div>
          )}
          {below}
        </div>
        {aside}
      </div>
    </>
  );
}
